import { useState, useRef, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { BadgeInput } from "@/components/ui/badgeInput";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";

type CoreIdentityStepProps = {
  setStep: (step: number) => void;
  onSubmit: () => void;
};

export default function CoreIdentityStep({
  setStep,
  onSubmit,
}: CoreIdentityStepProps) {
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [isInterestsFocused, setIsInterestsFocused] = useState(false);
  const interestsRef = useRef<HTMLDivElement>(null);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();
  const interests = watch("interests") || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        interestsRef.current &&
        !interestsRef.current.contains(event.target as Node)
      ) {
        setIsInterestsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInterestsFocus = () => {
    setIsInterestsFocused(!isInterestsFocused);
  };

  const handleFocus = (inputName: string) => {
    setFocusedInput(inputName);
  };

  const handleBlur = () => {
    setFocusedInput(null);
  };

  const handleInterestsChange = (newInterests: string[]) => {
    setValue("interests", newInterests, { shouldValidate: true });
  };

  return (
    <>
      <div className="flex flex-col">
        <h2 className="text-5xl font-bold mb-20 text-white text-center">
          CORE IDENTITY
        </h2>

        <div className="space-y-8 mb-16 max-w-md mx-auto w-full">
          <FormField
            control={control}
            name="interests"
            render={() => (
              <FormItem>
                <div ref={interestsRef} onClick={handleInterestsFocus}>
                  <Label
                    className={`block text-md font-medium mb-2 ${isInterestsFocused ? "text-primary" : "text-gray-300"}`}
                  >
                    INTERESTS
                  </Label>
                  <BadgeInput
                    value={interests}
                    onChange={handleInterestsChange}
                    placeholder="Add interest"
                    variant="colorful"
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="personality"
            render={({ field }) => (
              <FormItem>
                <Label
                  htmlFor="personality"
                  className={`block text-md font-medium mb-2 ${focusedInput === "personality" ? "text-primary" : "text-gray-300"}`}
                >
                  PERSONALITY
                </Label>
                <Textarea
                  id="personality"
                  placeholder="Write personality"
                  className={`w-full px-4 py-4 rounded border border-[#333333] text-white min-h-[120px] resize-none ${errors.personality ? "border-red-500" : ""}`}
                  onFocus={() => handleFocus("personality")}
                  {...field}
                  onBlur={() => {
                    handleBlur();
                    field.onBlur();
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="max-w-md mx-auto w-full pb-12">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <Button
            onClick={() => setStep(1)}
            variant="outline"
            className="w-full sm:min-w-[180px] md:min-w-[220px] py-2 sm:py-3 px-4 sm:px-6 min-h-[48px] text-center font-semibold text-base sm:text-lg"
          >
            BACK
          </Button>

          <Button
            variant="default"
            className="w-full sm:min-w-[180px] md:min-w-[220px] py-2 sm:py-3 px-4 sm:px-6 min-h-[48px] text-center font-semibold text-base sm:text-lg"
            onClick={onSubmit}
          >
            CREATE AGENT
          </Button>
        </div>
      </div>
    </>
  );
}
